import {
  InputText,
  ListBox,
  Skeleton,
} from '@agro1desenvolvimento/react-components';
import { useMemo, useRef, useState } from 'react';
import { useImmutableCallback } from '@agro1desenvolvimento/react-hooks';
import { sortBy } from 'lodash';
import { useDispatch } from 'react-redux';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import backgroundImage from '../../../assets/background-select-propriedade.jpg';
import type { Propriedade, Talhao } from '../../../@types';
import SideBarRight from '../../../components/SideBarRight';
import { showSideBarRight } from '../../../components/SideBarRight/sidebar-right-slice';
import { gridSizes } from './propriedade-card';
import CardsSkeleton from './cards-skeleton';
import PropriedadeCardItem from './propriedade-card-item';
import { PermissoesService } from '../../../services';
import UsageStatisticsDialog from '../usage-statistics-dialog';
import ContratoAlertasComponent from '../contrato-alertas';
import PropriedadeNotificacoes, {
  type PropriedadeNotificacoesRef,
} from './propriedade-notificacoes';
import GlebaItem, { GLEBA_TOOLTIP_TARGETS } from './gleba-item';
import useAlertasTalhao from './use-alertas-talhao';
import usePropriedadeSelect from './use-propriedade-select';
import { redirectToGleba, getDiasUltimaAcao } from './propriedade-select-utils';

const PropriedadeSelectPage = () => {
  const {
    search,
    setSearch,
    loading,
    hasError,
    historicos,
    markAsRead,
    filteredPropriedades,
  } = usePropriedadeSelect();

  const [propriedade, setPropriedade] = useState<Propriedade>();
  const [showUsageStats, setShowUsageStats] = useState(false);
  const notificacoesRef = useRef<PropriedadeNotificacoesRef>(null);
  const dispatch = useDispatch();

  const { alertasTalhoes, alertasLoading, alertasError, loadAlertas } =
    useAlertasTalhao();

  const glebas = useMemo(() => {
    const list = propriedade?.glebas ?? [];
    if (alertasTalhoes.length === 0) return sortBy(list, 'descricao');

    return [...list].sort((gleba, outraGleba) => {
      const dias = getDiasUltimaAcao(gleba, alertasTalhoes);
      const diasOutra = getDiasUltimaAcao(outraGleba, alertasTalhoes);
      if (dias !== diasOutra) return dias - diasOutra;
      return gleba.descricao.localeCompare(outraGleba.descricao);
    });
  }, [propriedade, alertasTalhoes]);

  const glebaItemTemplate = useImmutableCallback((gleba: Talhao) => (
    <GlebaItem
      gleba={gleba}
      alerta={alertasTalhoes.find((alerta) => alerta.id === gleba.id)}
      alertasLoading={alertasLoading}
      alertasError={alertasError}
    />
  ));

  const openSideBar = useImmutableCallback(
    (propriedadeSelected: Propriedade) => {
      setPropriedade(propriedadeSelected);
      dispatch(showSideBarRight());
      loadAlertas(propriedadeSelected.id);
    },
  );

  const showEmpty = !loading && !hasError && filteredPropriedades.length === 0;

  return (
    <div className="propriedade-select-page">
      <div className="grid p-4 grid-nogutter">
        <div className="col-12">
          <div
            className={`${gridSizes} search-input mb-3 mt-2 flex align-items-center gap-2`}
          >
            <span className="p-input-icon-left flex-1">
              <i className="pi pi-search" />
              <InputText
                autoFocus
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Filtrar"
                disabled={loading || !!hasError}
              />
            </span>
            {PermissoesService.parameters.manage && (
              <Button
                label="Estatísticas de uso"
                className="p-button-info"
                onClick={() => setShowUsageStats(true)}
              />
            )}
          </div>
        </div>

        <ContratoAlertasComponent />

        {loading && <CardsSkeleton />}

        {showEmpty && (
          <div className="propriedades-empty">
            <i className="pi pi-map mb-3" />
            <p>
              {search
                ? `Nenhuma propriedade encontrada para "${search}".`
                : 'Nenhuma propriedade disponível.'}
            </p>
          </div>
        )}

        {!loading &&
          filteredPropriedades.map((propriedadeIterator) => {
            const historico = historicos.find(
              (historicoItem) =>
                historicoItem.propriedade === propriedadeIterator.id,
            );
            return (
              <PropriedadeCardItem
                key={propriedadeIterator.uuid}
                propriedade={propriedadeIterator}
                historico={historico}
                onNotificacoesClick={(target) =>
                  notificacoesRef.current?.toggle(
                    target,
                    propriedadeIterator.id,
                    propriedadeIterator.descricao,
                  )
                }
                onMapaClick={() => openSideBar(propriedadeIterator)}
              />
            );
          })}
      </div>

      <SideBarRight header={propriedade?.descricao}>
        <Tooltip target={GLEBA_TOOLTIP_TARGETS.infoIcon} />
        <Tooltip target={GLEBA_TOOLTIP_TARGETS.badge} />
        <a href={`/#!/propriedade?propriedade=${propriedade?.uuid}`}>
          <img src={propriedade?.mapaUrl} alt="Mapa" width="100%" />
        </a>
        {alertasLoading ? (
          <div className="glebas-skeleton">
            {(propriedade?.glebas ?? []).map((gleba) => (
              <div key={gleba.id} className="glebas-skeleton-item">
                <Skeleton width="60%" height="0.85rem" />
                <Skeleton width="40%" height="0.75rem" />
              </div>
            ))}
          </div>
        ) : (
          <ListBox
            itemTemplate={glebaItemTemplate}
            options={glebas}
            onChange={(item) => {
              if (propriedade) redirectToGleba(propriedade, item.value);
            }}
          />
        )}
      </SideBarRight>

      <img src={backgroundImage} alt="" className="background" />

      <UsageStatisticsDialog
        visible={showUsageStats}
        onHide={() => setShowUsageStats(false)}
      />

      <PropriedadeNotificacoes ref={notificacoesRef} onRead={markAsRead} />
    </div>
  );
};

export default PropriedadeSelectPage;
